import { io } from 'socket.io-client';
import { remote_url } from "../constants/api";

export let pc_id: string | null = null;
let idListeners: ((id: string) => void)[] = [];

export const onIdReady = (callback: (id: string) => void) => {
    if (pc_id) {
        callback(pc_id);
        return () => {};
    }

    idListeners.push(callback);
    return () => {
        idListeners = idListeners.filter(fn => fn !== callback);
    };
};

export const localSocket = io('http://localhost:21579', {
    transports: ['websocket', 'polling'],
    tryAllTransports: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    timeout: 10000,
});

export const remoteSocket = io(`${remote_url}`, {
    transports: ['websocket', 'polling'],
    tryAllTransports: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
    timeout: 20000,
});

let lastRemoteJoinKey = '';
const LOCAL_RECOVERY_GRACE_MS = 10000;
let localFailureTimer: ReturnType<typeof setTimeout> | null = null;
let remoteFailureTimer: ReturnType<typeof setTimeout> | null = null;

export const joinRemoteDeviceRooms = (deviceId: string | null = pc_id, force = false) => {
    if (!deviceId || !remoteSocket.connected) return false;

    const cleanDeviceId = String(deviceId).trim();
    if (!cleanDeviceId) return false;

    const joinKey = `${remoteSocket.id || "pending"}:${cleanDeviceId}`;
    if (!force && lastRemoteJoinKey === joinKey) {
        return false;
    }

    lastRemoteJoinKey = joinKey;

    remoteSocket.emit("joinDevice", cleanDeviceId);
    // The launcher needs device-scoped control events. The game page alone owns
    // the exclusive game session and claims it when the player opens a game.
    return true;
};

const enterRecoveryMode = (title: string, message: string) => {
    document.body.dataset.inError = "true";
    window.dispatchEvent(new CustomEvent("system_failure", {
        detail: { title, message, isRecovering: true }
    }));
};

const clearFailureTimer = (timer: ReturnType<typeof setTimeout> | null) => {
    if (timer) {
        clearTimeout(timer);
    }
};

const handleRecoveredConnection = () => {
    if (!localSocket.connected || !remoteSocket.connected) return;

    delete document.body.dataset.inError;
    window.dispatchEvent(new CustomEvent("system_recovered"));
};

window.addEventListener('offline', () => {
    enterRecoveryMode(
        "NETWORK OFFLINE",
        "Your internet connection is down. Please check your local network settings."
    );
});

localSocket.on("connect", () => {
    clearFailureTimer(localFailureTimer);
    localFailureTimer = null;
    localSocket.emit("requestStatus", "sync");
    handleRecoveredConnection();
});

remoteSocket.on("connect", () => {
    clearFailureTimer(remoteFailureTimer);
    remoteFailureTimer = null;
    joinRemoteDeviceRooms();
    handleRecoveredConnection();
});

localSocket.on("disconnect", (reason) => {
    clearFailureTimer(localFailureTimer);
    localFailureTimer = setTimeout(() => {
        if (!navigator.onLine) return;
        if (localSocket.connected) return;
        if (reason !== "io client disconnect" && document.body.dataset.inError !== "true") {
            enterRecoveryMode(
                "LOCAL LINK FAILURE",
                "Hardware bridge connection lost. Waiting for local service to resume..."
            );
        }
    }, LOCAL_RECOVERY_GRACE_MS);
});

localSocket.on("connect_error", () => {
    clearFailureTimer(localFailureTimer);
    localFailureTimer = setTimeout(() => {
        if (!navigator.onLine) return;
        if (localSocket.connected) return;
        if (document.body.dataset.inError !== "true") {
            enterRecoveryMode(
                "LOCAL LINK FAILURE",
                "Hardware bridge unreachable. Waiting for local service to resume. Try restarting the launcher app"
            );
        }
    }, LOCAL_RECOVERY_GRACE_MS);
});

remoteSocket.on("disconnect", (reason) => {
    lastRemoteJoinKey = '';
    clearFailureTimer(remoteFailureTimer);
    remoteFailureTimer = setTimeout(() => {
        if (!navigator.onLine) return;
        if (remoteSocket.connected) return;
        if (reason !== "io client disconnect") {
            enterRecoveryMode(
                "DEVICE CONNECTION INTERRUPTED",
                "This device is reconnecting to the game. Please keep it online."
            );
        }
    }, 10000);
});

remoteSocket.on("connect_error", () => {
    clearFailureTimer(remoteFailureTimer);
    remoteFailureTimer = setTimeout(() => {
        if (!navigator.onLine) return;
        if (remoteSocket.connected) return;
        if (document.body.dataset.inError !== "true") {
            enterRecoveryMode(
                "DEVICE CONNECTION INTERRUPTED",
                "This device is reconnecting to the game. Please keep it online."
            );
        }
    }, 10000);
});

remoteSocket.on("deviceReboot",()=>{
    window.location.reload();
});

localSocket.on("pc_stats", (data) => {
    const deviceId = String(data?.pcId || '').trim();
    if (!deviceId) return;

    const changed = pc_id !== deviceId;
    pc_id = deviceId;
    if (changed) {
        const listeners = idListeners;
        idListeners = [];
        listeners.forEach(fn => fn(deviceId));
    }
    joinRemoteDeviceRooms(deviceId);
});
